import { FTCEnabled } from '@/constants/ftc';
import { warning } from '@asurion-core/invariant';

export interface Step {
  next?: string;
  previous?: string;
  required?: boolean;
  requiredEntities?: string[];
}

const PRODUCT_KEYS = [
  'appliance-plus',
  'appliance-protect',
  'home-entertainment',
  'home-plus',
  'home-tech-protect',
  'renter-tech-protect',
] as const;

const PARTNER_KEYS = [
  'asurion',
  'cricket',
  'att',
  'comparion',
  'real-page',
] as const;

const PRESENTATION_KEYS = ['app', 'affiliate', 'gift', 'prepaid'] as const;

export type ProductKey = (typeof PRODUCT_KEYS)[number];
export type PartnerKey = (typeof PARTNER_KEYS)[number];
export type PresentationKey = (typeof PRESENTATION_KEYS)[number];

export type locales = 'en-US' | 'es' | 'es-US';
export type Steps = { steps: Record<string, Step> };
export type ProductInfo = {
  product?: {
    name?: string;
    allowedLocales: locales[];
    dashboardUrl?: string;
    termsAndConditionsUrl?: string;
    termsOfServiceUrl?: string;
    onlineServicingSupported?: boolean;
    /**
     * Enables a checkbox on the account-details page for SMS opt-in and adds a
     * link to the SMS T&C's on one of the review page checkboxes.
     *
     * It will send the SMS opt-in value to the EIP as a boolean so marketing
     * can use it to send marketing messages to the customer.
     */
    smsOptIn?: boolean;
    customerSMSTemplateKey?: string;
  };
};
export type ProductPartnerPresentationConfig = ProductInfo & Steps;

export interface TestConfig {
  aspUrlParamMap: Record<string, string>;
  products: Record<
    string,
    Record<
      string,
      ProductInfo &
        Partial<{ [key in PresentationKey]: ProductPartnerPresentationConfig }>
    >
  >;
}

export const testConfig: TestConfig = {
  aspUrlParamMap: {
    mvid: 'mdnVerification',
    poid: 'productOffer',
    psid: 'paySession',
  },
  products: {
    'appliance-plus': {
      asurion: {
        product: {
          allowedLocales: ['en-US'],
          dashboardUrl: 'https://my.asurion.com/dashboard?mxclient=asurion',
          termsAndConditionsUrl:
            'https://www.asurion.com/applianceplus/appliance-terms-and-conditions',
          smsOptIn: true,
        },
        app: {
          steps: FTCEnabled
            ? {
                'personal-info': {
                  next: 'account-details',
                  previous: undefined,
                  required: true,
                  requiredEntities: [],
                },
                'account-details': {
                  next: 'payment',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: ['customerSession'],
                },
              }
            : {
                confirmation: {
                  next: undefined,
                  previous: 'review',
                  required: false,
                  requiredEntities: [],
                },
                'account-details': {
                  next: 'payment',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: ['customerSession'],
                },
                payment: {
                  next: 'program-info',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: ['customerSession', 'productOffer'],
                },
                'personal-info': {
                  next: 'account-details',
                  previous: undefined,
                  required: true,
                  requiredEntities: [],
                },
                'program-info': {
                  next: 'review',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: [
                    'customerSession',
                    'paySession',
                    'productOffer',
                  ],
                },
                review: {
                  next: 'confirmation',
                  previous: 'program-info',
                  required: true,
                  requiredEntities: [
                    'customerSession',
                    'paySession',
                    'productOffer',
                  ],
                },
              },
        },
        affiliate: {
          steps: {
            confirmation: {
              next: undefined,
              previous: 'review',
              required: false,
              requiredEntities: [],
            },
            'account-details': {
              next: 'payment',
              previous: 'personal-info',
              required: true,
              requiredEntities: ['customerSession'],
            },
            payment: {
              next: 'program-info',
              previous: 'personal-info',
              required: true,
              requiredEntities: ['customerSession', 'productOffer'],
            },
            'personal-info': {
              next: 'account-details',
              previous: undefined,
              required: true,
              requiredEntities: [],
            },
            'program-info': {
              next: 'review',
              previous: 'personal-info',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
            review: {
              next: 'confirmation',
              previous: 'program-info',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
          },
        },
      },
    },
    'appliance-protect': {
      comparion: {
        product: {
          allowedLocales: ['en-US'],
          termsAndConditionsUrl:
            'https://partners.affiliates.asurion.com/terms-conditions/appliance-protect/comparion',
          onlineServicingSupported: false,
        },
        affiliate: {
          product: {
            allowedLocales: ['en-US'],
            termsAndConditionsUrl:
              'https://partners.affiliates.asurion.com/terms-conditions/appliance-protect/comparion',
            onlineServicingSupported: false,
          },
          steps: {
            confirmation: {
              next: undefined,
              previous: 'review',
              required: true,
              requiredEntities: [],
            },
            'account-details': {
              next: 'payment',
              previous: 'personal-info',
              required: true,
              requiredEntities: ['customerSession'],
            },
            payment: {
              next: 'program-info',
              previous: 'personal-info',
              required: true,
              requiredEntities: ['customerSession', 'productOffer'],
            },
            'personal-info': {
              next: 'account-details',
              previous: undefined,
              required: true,
              requiredEntities: [],
            },
            'program-info': {
              next: 'review',
              previous: 'personal-info',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
            review: {
              next: 'confirmation',
              previous: 'program-info',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
          },
        },
        gift: {
          product: {
            allowedLocales: ['en-US'],
            termsAndConditionsUrl:
              'https://partners.affiliates.asurion.com/terms-conditions/appliance-protect/comparion',
            termsOfServiceUrl:
              'https://www.asurion.com/pdf/appliance-terms-of-service/',
            onlineServicingSupported: false,
          },
          steps: {
            'personal-account-details': {
              next: 'program-info',
              previous: undefined,
              required: true,
              requiredEntities: ['customerSession'],
            },
            'program-info': {
              next: 'payment',
              previous: 'personal-account-details',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
            payment: {
              next: 'review',
              previous: 'program-info',
              required: true,
              requiredEntities: ['customerSession', 'productOffer'],
            },
            review: {
              next: 'confirmation',
              previous: 'program-info',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
            confirmation: {
              next: undefined,
              previous: 'review',
              required: true,
              requiredEntities: [],
            },
          },
        },
      },
      viu: {
        app: {
          product: {
            allowedLocales: ['en-US'],
            dashboardUrl: 'https://my.asurion.com/applianceplus',
            termsAndConditionsUrl: 'http://www.asurion.com/legal/viu',
            onlineServicingSupported: false,
          },
          steps: {
            'personal-info': {
              next: 'account-details',
              previous: undefined,
              required: true,
              requiredEntities: [],
            },
            'account-details': {
              next: 'program-info',
              previous: 'personal-info',
              required: true,
              requiredEntities: ['customerSession'],
            },
            'program-info': {
              next: 'payment',
              previous: 'account-details',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
            payment: {
              next: 'review',
              previous: 'program-info',
              required: true,
              requiredEntities: ['customerSession', 'productOffer'],
            },

            review: {
              next: 'confirmation',
              previous: 'program-info',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
            confirmation: {
              next: undefined,
              previous: 'review',
              required: true,
              requiredEntities: [],
            },
          },
        },
      },
    },
    'home-entertainment': {
      asurion: {
        product: {
          allowedLocales: ['en-US'],
          dashboardUrl: 'https://my.asurion.com/dashboard?mxclient=asurion',
          termsAndConditionsUrl:
            'https://www.asurion.com/pdf/homeplus-terms-conditions-080123/',
          termsOfServiceUrl:
            'https://www.asurion.com/pdf/homeplus-terms-of-services/',
        },
        app: {
          steps: FTCEnabled
            ? {
                'personal-info': {
                  next: 'account-details',
                  previous: undefined,
                  required: true,
                  requiredEntities: [],
                },
                'account-details': {
                  next: 'payment',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: ['customerSession'],
                },
              }
            : {
                confirmation: {
                  next: undefined,
                  previous: 'review',
                  required: false,
                  requiredEntities: [],
                },
                'account-details': {
                  next: 'payment',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: ['customerSession'],
                },
                payment: {
                  next: 'program-info',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: ['customerSession', 'productOffer'],
                },
                'personal-info': {
                  next: 'account-details',
                  previous: undefined,
                  required: true,
                  requiredEntities: [],
                },
                'program-info': {
                  next: 'review',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: [
                    'customerSession',
                    'paySession',
                    'productOffer',
                  ],
                },
                review: {
                  next: 'confirmation',
                  previous: 'program-info',
                  required: true,
                  requiredEntities: [
                    'customerSession',
                    'paySession',
                    'productOffer',
                  ],
                },
              },
        },
      },
    },
    'home-plus': {
      asurion: {
        product: {
          allowedLocales: ['en-US'],
          dashboardUrl: 'https://my.asurion.com/dashboard?mxclient=asurion',
          termsAndConditionsUrl:
            'https://www.asurion.com/pdf/homeplus-terms-conditions-080123/',
          termsOfServiceUrl:
            'https://www.asurion.com/pdf/homeplus-terms-of-services/',
        },
        app: {
          product: {
            allowedLocales: ['en-US'],
            dashboardUrl: 'https://my.asurion.com/dashboard?mxclient=asurion',
            termsAndConditionsUrl:
              'https://www.asurion.com/pdf/homeplus-terms-conditions-080123/',
            termsOfServiceUrl:
              'https://www.asurion.com/pdf/homeplus-terms-of-services/',
            smsOptIn: true,
          },

          steps: FTCEnabled
            ? {
                'personal-info': {
                  next: 'account-details',
                  previous: undefined,
                  required: true,
                  requiredEntities: [],
                },
                'account-details': {
                  next: 'payment',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: ['customerSession'],
                },
              }
            : {
                confirmation: {
                  next: undefined,
                  previous: 'review',
                  required: false,
                  requiredEntities: [],
                },
                'account-details': {
                  next: 'payment',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: ['customerSession'],
                },
                payment: {
                  next: 'program-info',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: ['customerSession', 'productOffer'],
                },
                'personal-info': {
                  next: 'account-details',
                  previous: undefined,
                  required: true,
                  requiredEntities: [],
                },
                'program-info': {
                  next: 'review',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: [
                    'customerSession',
                    'paySession',
                    'productOffer',
                  ],
                },
                review: {
                  next: 'confirmation',
                  previous: 'program-info',
                  required: true,
                  requiredEntities: [
                    'customerSession',
                    'paySession',
                    'productOffer',
                  ],
                },
              },
        },
        affiliate: {
          steps: {
            confirmation: {
              next: undefined,
              previous: 'review',
              required: false,
              requiredEntities: [],
            },
            'account-details': {
              next: 'payment',
              previous: 'personal-info',
              required: true,
              requiredEntities: ['customerSession'],
            },
            payment: {
              next: 'program-info',
              previous: 'personal-info',
              required: true,
              requiredEntities: ['customerSession', 'productOffer'],
            },
            'personal-info': {
              next: 'account-details',
              previous: undefined,
              required: true,
              requiredEntities: [],
            },
            'program-info': {
              next: 'review',
              previous: 'personal-info',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
            review: {
              next: 'confirmation',
              previous: 'program-info',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
          },
        },
      },
      comparion: {
        product: {
          allowedLocales: ['en-US'],
          dashboardUrl: 'https://my.asurion.com/dashboard?mxclient=comparion',
          termsAndConditionsUrl:
            'https://partners.affiliates.asurion.com/terms-conditions/home-plus/comparion',
          termsOfServiceUrl:
            'https://www.asurion.com/pdf/homeplus-terms-of-services/',
        },
        app: {
          product: {
            allowedLocales: ['en-US'],
            dashboardUrl: 'https://my.asurion.com/dashboard?mxclient=comparion',
            termsAndConditionsUrl:
              'https://partners.affiliates.asurion.com/terms-conditions/home-plus/comparion',
            termsOfServiceUrl:
              'https://www.asurion.com/pdf/homeplus-terms-of-services/',
          },
          steps: {
            confirmation: {
              next: undefined,
              previous: 'review',
              required: true,
              requiredEntities: [],
            },
            'account-details': {
              next: 'payment',
              previous: 'personal-info',
              required: true,
              requiredEntities: ['customerSession'],
            },
            payment: {
              next: 'program-info',
              previous: 'account-details',
              required: true,
              requiredEntities: ['customerSession', 'productOffer'],
            },
            'personal-info': {
              next: 'account-details',
              previous: undefined,
              required: true,
              requiredEntities: [],
            },
            'program-info': {
              next: 'review',
              previous: 'payment',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
            review: {
              next: 'confirmation',
              previous: 'program-info',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
          },
        },
      },
      cricket: {
        app: {
          product: {
            allowedLocales: ['en-US'],
            dashboardUrl: 'https://my.asurion.com/dashboard?mxclient=asurion',
            termsAndConditionsUrl:
              'https://www.asurion.com/pdf/homeplus-terms-conditions-080123/',
            termsOfServiceUrl:
              'https://www.asurion.com/pdf/homeplus-terms-of-services/',
          },
          steps: FTCEnabled
            ? {
                'personal-info': {
                  next: 'account-details',
                  previous: undefined,
                  required: true,
                  requiredEntities: [],
                },
                'account-details': {
                  next: 'payment',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: ['customerSession'],
                },
              }
            : {
                confirmation: {
                  next: undefined,
                  previous: 'review',
                  required: false,
                  requiredEntities: [],
                },
                'account-details': {
                  next: 'payment',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: ['customerSession'],
                },
                payment: {
                  next: 'program-info',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: ['customerSession', 'productOffer'],
                },
                'personal-info': {
                  next: 'account-details',
                  previous: undefined,
                  required: true,
                  requiredEntities: [],
                },
                'program-info': {
                  next: 'review',
                  previous: 'personal-info',
                  required: true,
                  requiredEntities: [
                    'customerSession',
                    'paySession',
                    'productOffer',
                  ],
                },
                review: {
                  next: 'confirmation',
                  previous: 'program-info',
                  required: true,
                  requiredEntities: [
                    'customerSession',
                    'paySession',
                    'productOffer',
                  ],
                },
              },
        },
      },
      viu: {
        app: {
          product: {
            allowedLocales: ['en-US'],
            dashboardUrl: 'https://my.asurion.com/dashboard?mxclient=asurion',
            termsAndConditionsUrl: 'http://www.asurion.com/legal/viu',
            termsOfServiceUrl: 'http://www.asurion.com/legal/viu',
          },
          steps: {
            confirmation: {
              next: undefined,
              previous: 'review',
              required: true,
              requiredEntities: [],
            },
            'account-details': {
              next: 'payment',
              previous: 'personal-info',
              required: true,
              requiredEntities: ['customerSession'],
            },
            payment: {
              next: 'program-info',
              previous: 'account-details',
              required: true,
              requiredEntities: ['customerSession', 'productOffer'],
            },
            'personal-info': {
              next: 'account-details',
              previous: undefined,
              required: true,
              requiredEntities: [],
            },
            'program-info': {
              next: 'review',
              previous: 'payment',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
            review: {
              next: 'confirmation',
              previous: 'program-info',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
          },
        },
      },
    },
    'home-tech-protect': {
      att: {
        product: {
          allowedLocales: ['en-US'],
          dashboardUrl: 'https://my.asurion.com/dashboard?mxclient=att',
          termsAndConditionsUrl:
            '/files/att-home-tech-protection-terms-of-service-visits-final-072522.pdf',
        },
        app: {
          steps: FTCEnabled
            ? {
                'account-lookup': {
                  next: 'verify-otp',
                  previous: undefined,
                  required: true,
                  requiredEntities: [],
                },
                'verify-otp': {
                  next: 'program-info',
                  previous: 'account-lookup',
                  required: true,
                  requiredEntities: ['customerSession', 'mdnVerification'],
                },
              }
            : {
                'account-lookup': {
                  next: 'verify-otp',
                  previous: undefined,
                  required: true,
                  requiredEntities: [],
                },
                'verify-otp': {
                  next: 'program-info',
                  previous: 'account-lookup',
                  required: true,
                  requiredEntities: ['customerSession', 'mdnVerification'],
                },
                'program-info': {
                  next: 'review',
                  previous: 'account-lookup',
                  required: true,
                  requiredEntities: [
                    'customerSession',
                    'paySession',
                    'productOffer',
                  ],
                },
                review: {
                  next: 'confirmation',
                  previous: 'account-lookup',
                  required: true,
                  requiredEntities: [],
                },
                confirmation: {
                  next: undefined,
                  previous: 'review',
                  required: false,
                  requiredEntities: [],
                },
              },
        },
      },
    },
    'mobile-insurance': {
      att: {
        product: {
          allowedLocales: ['en-US', 'es', 'es-US'],
          customerSMSTemplateKey: 'HZN_PREPAID_ENROLL_REMINDER',
          dashboardUrl: 'https://my.asurion.com/dashboard?mxclient=att',
        },
        prepaid: {
          steps: FTCEnabled
            ? {
                'choose-plan': {
                  next: 'account-lookup',
                  previous: undefined,
                  required: true,
                  requiredEntities: [],
                },
                'account-lookup': {
                  next: 'verify-otp',
                  previous: 'choose-plan',
                  required: true,
                  requiredEntities: [],
                },
                'verify-otp': {
                  next: 'personal-account-details',
                  previous: 'account-lookup',
                  required: true,
                  requiredEntities: ['customerSession', 'mdnVerification'],
                },
                'personal-account-details': {
                  next: 'device-confirmation',
                  previous: 'account-lookup',
                  required: true,
                  requiredEntities: [
                    'customerSession',
                    'paySession',
                    'productOffer',
                  ],
                },
                'device-confirmation': {
                  next: 'device-condition',
                  previous: 'personal-account-details',
                  required: true,
                  requiredEntities: [],
                },
                'device-condition': {
                  next: 'payment',
                  previous: 'device-confirmation',
                  required: true,
                  requiredEntities: [],
                },
                'device-selection': {
                  next: 'device-condition',
                  previous: 'device-confirmation',
                  required: false,
                  requiredEntities: [],
                },
              }
            : {
                confirmation: {
                  next: undefined,
                  previous: 'review',
                  required: false,
                  requiredEntities: [],
                },
                'choose-plan': {
                  next: 'account-lookup',
                  previous: undefined,
                  required: true,
                  requiredEntities: [],
                },
                'account-lookup': {
                  next: 'verify-otp',
                  previous: 'choose-plan',
                  required: true,
                  requiredEntities: [],
                },
                'verify-otp': {
                  next: 'personal-account-details',
                  previous: 'account-lookup',
                  required: true,
                  requiredEntities: ['customerSession', 'mdnVerification'],
                },
                'personal-account-details': {
                  next: 'device-confirmation',
                  previous: 'account-lookup',
                  required: true,
                  requiredEntities: [
                    'customerSession',
                    'paySession',
                    'productOffer',
                  ],
                },
                'device-confirmation': {
                  next: 'device-condition',
                  previous: 'personal-account-details',
                  required: true,
                  requiredEntities: [],
                },
                'device-selection': {
                  next: 'device-condition',
                  previous: 'device-confirmation',
                  required: false,
                  requiredEntities: [],
                },
                'device-condition': {
                  next: 'payment',
                  previous: 'device-confirmation',
                  required: true,
                  requiredEntities: [],
                },
                payment: {
                  next: 'review',
                  previous: 'device-condition',
                  required: true,
                  requiredEntities: [],
                },
                review: {
                  next: 'confirmation',
                  previous: 'payment',
                  required: true,
                  requiredEntities: [],
                },
              },
        },
      },
    },
    'mobile-protection': {
      att: {
        product: {
          allowedLocales: ['en-US', 'es', 'es-US'],
          customerSMSTemplateKey: 'HZN_PREPAID_ENROLL_REMINDER',
          dashboardUrl: 'https://my.asurion.com/dashboard?mxclient=att',
        },
        prepaid: {
          steps: FTCEnabled
            ? {
                'choose-plan': {
                  next: 'account-lookup',
                  previous: undefined,
                  required: true,
                  requiredEntities: [],
                },
                'account-lookup': {
                  next: 'verify-otp',
                  previous: 'choose-plan',
                  required: true,
                  requiredEntities: [],
                },
                'verify-otp': {
                  next: 'personal-account-details',
                  previous: 'account-lookup',
                  required: true,
                  requiredEntities: ['customerSession', 'mdnVerification'],
                },
                'personal-account-details': {
                  next: 'device-confirmation',
                  previous: 'account-lookup',
                  required: true,
                  requiredEntities: [
                    'customerSession',
                    'paySession',
                    'productOffer',
                  ],
                },
                'device-confirmation': {
                  next: 'device-condition',
                  previous: 'personal-account-details',
                  required: true,
                  requiredEntities: [],
                },
                'device-condition': {
                  next: 'payment',
                  previous: 'device-confirmation',
                  required: true,
                  requiredEntities: [],
                },
              }
            : {
                confirmation: {
                  next: undefined,
                  previous: 'review',
                  required: false,
                  requiredEntities: [],
                },
                'choose-plan': {
                  next: 'account-lookup',
                  previous: undefined,
                  required: true,
                  requiredEntities: [],
                },
                'account-lookup': {
                  next: 'verify-otp',
                  previous: 'choose-plan',
                  required: true,
                  requiredEntities: [],
                },
                'verify-otp': {
                  next: 'personal-account-details',
                  previous: 'account-lookup',
                  required: true,
                  requiredEntities: ['customerSession', 'mdnVerification'],
                },
                'personal-account-details': {
                  next: 'device-confirmation',
                  previous: 'account-lookup',
                  required: true,
                  requiredEntities: [
                    'customerSession',
                    'paySession',
                    'productOffer',
                  ],
                },
                'device-confirmation': {
                  next: 'device-condition',
                  previous: 'personal-account-details',
                  required: true,
                  requiredEntities: [],
                },
                'device-selection': {
                  next: 'device-condition',
                  previous: 'device-confirmation',
                  required: false,
                  requiredEntities: [],
                },
                'device-condition': {
                  next: 'payment',
                  previous: 'device-confirmation',
                  required: true,
                  requiredEntities: [],
                },
                payment: {
                  next: 'review',
                  previous: 'device-condition',
                  required: true,
                  requiredEntities: [],
                },
                review: {
                  next: 'confirmation',
                  previous: 'payment',
                  required: true,
                  requiredEntities: [],
                },
              },
        },
      },
    },
    'renter-tech-protect': {
      'real-page': {
        product: {
          name: 'Renter Tech Protect',
          allowedLocales: ['en-US'],
          termsAndConditionsUrl: 'https://www.asurion.com/legal/realpage/',
        },
        affiliate: {
          steps: {
            'personal-account-details': {
              next: 'program-info',
              previous: undefined,
              required: true,
              requiredEntities: ['customerSession'],
            },
            'program-info': {
              next: 'payment',
              previous: 'personal-account-details',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
            payment: {
              next: 'review',
              previous: 'program-info',
              required: true,
              requiredEntities: ['customerSession', 'productOffer'],
            },
            review: {
              next: 'confirmation',
              previous: 'program-info',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
            confirmation: {
              next: undefined,
              previous: 'review',
              required: true,
              requiredEntities: [],
            },
          },
        },
      },
    },
    'renter-tech-support': {
      'real-page': {
        product: {
          name: 'Renter Tech Support',
          allowedLocales: ['en-US'],
          termsAndConditionsUrl: 'https://www.asurion.com/legal/realpage/',
        },
        affiliate: {
          steps: {
            'personal-account-details': {
              next: 'program-info',
              previous: undefined,
              required: true,
              requiredEntities: ['customerSession'],
            },
            'program-info': {
              next: 'payment',
              previous: 'personal-account-details',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
            payment: {
              next: 'review',
              previous: 'program-info',
              required: true,
              requiredEntities: ['customerSession', 'productOffer'],
            },
            review: {
              next: 'confirmation',
              previous: 'program-info',
              required: true,
              requiredEntities: [
                'customerSession',
                'paySession',
                'productOffer',
              ],
            },
            confirmation: {
              next: undefined,
              previous: 'review',
              required: true,
              requiredEntities: [],
            },
          },
        },
      },
    },
  },
};

export function getStepConfig({
  product,
  partner,
  presentation,
}: {
  product: string;
  partner: string;
  presentation: PresentationKey;
}): Record<string, Step> | undefined {
  if (!testConfig.products?.[product]?.[partner]?.[presentation]) {
    return undefined;
  }

  return testConfig.products[product][partner][presentation].steps;
}

export function getProductInfo({
  product,
  partner,
  presentation,
}: {
  product: string;
  partner: string;
  presentation?: PresentationKey;
}): ProductInfo | undefined {
  if (!presentation) {
    return testConfig.products?.[product]?.[partner]?.product
      ? { product: testConfig.products[product][partner].product }
      : undefined;
  }

  return {
    product:
      testConfig.products?.[product]?.[partner]?.[presentation]?.product ??
      testConfig.products?.[product]?.[partner]?.product,
  };
}

export interface StepTotals {
  total: number;
  completeTotal: number;
  currentStepIndex: number;
  incompleteTotal: number;
}

export function getStepTotals({
  currentStep,
  steps,
  requiredOnly = false,
}: {
  steps: Record<string, Step>;
  currentStep: string;
  requiredOnly?: boolean;
}): StepTotals {
  let stepsList = Object.keys(steps).map((stepName) => ({
    ...steps[stepName],
    value: stepName,
  }));

  warning(
    !!steps[currentStep] &&
      stepsList.find((step) => step.value === currentStep),
    `The current step "${currentStep}" was not found.`,
  );

  if (requiredOnly) {
    stepsList = stepsList.filter((step) => step.required);
  }

  const total = stepsList.length;
  const findCurrentIndex = () => {
    let stepNode = stepsList.find((step) => step.previous === undefined);
    let index = 0;
    while (stepNode) {
      if (stepNode.value === currentStep) {
        return index;
      }

      stepNode =
        stepNode.next && stepsList.some((step) => step.value === stepNode?.next)
          ? {
              ...steps[stepNode.next],
              value: stepNode.next,
            }
          : undefined;
      index++;
    }
    return -1;
  };
  const currentStepIndex = findCurrentIndex();
  const incompleteTotal =
    total > 0 && currentStepIndex >= 0 ? total - currentStepIndex - 1 : 0;

  return {
    total,
    completeTotal: currentStepIndex >= 0 ? currentStepIndex : 0,
    currentStepIndex,
    incompleteTotal,
  };
}

export function isProductKey(product: string): product is ProductKey {
  return PRODUCT_KEYS.includes(product as ProductKey);
}

export function isPartnerKey(partner: string): partner is PartnerKey {
  return PARTNER_KEYS.includes(partner as PartnerKey);
}

export function isPresentationKey(
  presentation: string,
): presentation is PresentationKey {
  return PRESENTATION_KEYS.includes(presentation as PresentationKey);
}

export function assertProductKey(
  product: string,
): asserts product is ProductKey {
  warning(isProductKey(product), `Invalid product key: ${product}`);
}

export function assertPartnerKey(
  partner: string,
): asserts partner is PartnerKey {
  warning(isPartnerKey(partner), `Invalid partner key: ${partner}`);
}

export function assertPresentationKey(
  presentation: string,
): asserts presentation is PresentationKey {
  warning(
    isPresentationKey(presentation),
    `Invalid presentation key: ${presentation}`,
  );
}
